import React, { useState, useEffect } from 'react';
import { Cookie, X, Settings } from 'lucide-react';
import { Button } from '../ui/button';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always required
    analytics: false,
    preferences: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('gwins_cookie_consent');
    if (!consent) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      preferences: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('gwins_cookie_consent', JSON.stringify(allAccepted));
    setShowBanner(false);
  };

  const handleAcceptSelected = () => {
    const selected = {
      ...preferences,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('gwins_cookie_consent', JSON.stringify(selected));
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleRejectAll = () => {
    const essentialOnly = {
      essential: true,
      analytics: false,
      preferences: false,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('gwins_cookie_consent', JSON.stringify(essentialOnly));
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/50 z-[998]" onClick={() => {}} />
      
      {/* Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-[999] p-4 sm:p-6">
        <div className="max-w-4xl mx-auto bg-[#131416] rounded-2xl border border-white/10 shadow-2xl overflow-hidden">
          {!showSettings ? (
            /* Main Banner */
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[#00d4aa]/20 flex items-center justify-center flex-shrink-0">
                  <Cookie className="h-6 w-6 text-[#00d4aa]" />
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-bold text-lg mb-2">We Value Your Privacy</h3>
                  <p className="text-white/70 text-sm leading-relaxed">
                    We use cookies to enhance your browsing experience, provide personalized content, and analyze our traffic. 
                    By clicking "Accept All", you consent to our use of cookies. You can customize your preferences or 
                    reject non-essential cookies.
                  </p>
                  <a 
                    href="/privacy-policy" 
                    className="text-[#00d4aa] text-sm hover:underline mt-2 inline-block"
                  >
                    Learn more in our Privacy Policy
                  </a>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <Button
                  onClick={handleAcceptAll}
                  className="bg-[#00d4aa] hover:bg-[#33e0bb] text-[#0a0b0c] font-bold flex-1 sm:flex-none"
                >
                  Accept All
                </Button>
                <Button
                  onClick={() => setShowSettings(true)}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/5 flex-1 sm:flex-none"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Customize
                </Button>
                <Button
                  onClick={handleRejectAll}
                  variant="ghost"
                  className="text-white/60 hover:text-white hover:bg-white/5 flex-1 sm:flex-none"
                >
                  Reject All
                </Button>
              </div>
            </div>
          ) : (
            /* Settings Panel */
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-white font-bold text-lg">Cookie Preferences</h3>
                <button
                  onClick={() => setShowSettings(false)}
                  className="text-white/60 hover:text-white"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                {/* Essential Cookies */}
                <div className="flex items-start justify-between p-4 rounded-lg bg-white/5">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="text-white font-semibold">Essential Cookies</h4>
                      <span className="text-xs bg-[#00d4aa]/20 text-[#00d4aa] px-2 py-0.5 rounded">Required</span>
                    </div>
                    <p className="text-white/60 text-sm mt-1">
                      These cookies are necessary for the website to function and cannot be disabled.
                    </p>
                  </div>
                  <div className="w-12 h-6 bg-[#00d4aa] rounded-full relative cursor-not-allowed opacity-60">
                    <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full" />
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="flex items-start justify-between p-4 rounded-lg bg-white/5">
                  <div className="flex-1">
                    <h4 className="text-white font-semibold">Analytics Cookies</h4>
                    <p className="text-white/60 text-sm mt-1">
                      Help us understand how visitors interact with our website.
                    </p>
                  </div>
                  <button
                    onClick={() => setPreferences(p => ({ ...p, analytics: !p.analytics }))}
                    className={`w-12 h-6 rounded-full relative transition-colors ${
                      preferences.analytics ? 'bg-[#00d4aa]' : 'bg-white/20'
                    }`}
                  >
                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${
                      preferences.analytics ? 'right-1' : 'left-1'
                    }`} />
                  </button>
                </div>

                {/* Preference Cookies */}
                <div className="flex items-start justify-between p-4 rounded-lg bg-white/5">
                  <div className="flex-1">
                    <h4 className="text-white font-semibold">Preference Cookies</h4>
                    <p className="text-white/60 text-sm mt-1">
                      Remember your settings and preferences for a better experience.
                    </p>
                  </div>
                  <button
                    onClick={() => setPreferences(p => ({ ...p, preferences: !p.preferences }))}
                    className={`w-12 h-6 rounded-full relative transition-colors ${
                      preferences.preferences ? 'bg-[#00d4aa]' : 'bg-white/20'
                    }`}
                  >
                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${
                      preferences.preferences ? 'right-1' : 'left-1'
                    }`} />
                  </button>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button
                  onClick={handleAcceptSelected}
                  className="bg-[#00d4aa] hover:bg-[#33e0bb] text-[#0a0b0c] font-bold flex-1"
                >
                  Save Preferences
                </Button>
                <Button
                  onClick={handleAcceptAll}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/5 flex-1"
                >
                  Accept All
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CookieConsent;
